/**
 * Envío por email desde el formulario: el diálogo individual, el masivo y la
 * botonera que los abre.
 *
 * Es **genérico**: no sabe nada de facturas ni de consorcios. Todo lo que
 * dibuja sale de `spec.mailing`, que el backend arma a partir de lo que declaró
 * cada módulo (`nucleo/core/mailing.py`). Un módulo nuevo que declare un
 * `MailSource` obtiene este diálogo sin tocar una línea de acá.
 *
 * Cinco decisiones que definen su comportamiento:
 *
 * 1. **La plantilla propone, el usuario dispone.** El destinatario, el asunto y
 *    el cuerpo llegan renderizados desde la plantilla de la organización y los
 *    tres son editables antes de mandar. El texto de fábrica no es una camisa
 *    de fuerza.
 * 2. **Nadie recibe la misma liquidación dos veces.** Son dos candados y los dos
 *    son del backend. La `request_key` cubre el doble click sobre la pantalla
 *    abierta; el registro de envíos (`MailDelivery`) cubre lo otro —cerrar el
 *    diálogo, volver a abrirlo y disparar de nuevo—, que es lo que de verdad
 *    pasa en una oficina y lo que mandaba la expensa dos veces a los sesenta.
 *    Acá sólo se muestra: los que ya la recibieron llegan marcados, salen del
 *    lote y el botón dice el número real. Reenviarles es una casilla aparte,
 *    porque corregir una liquidación y volver a mandarla es legítimo — lo que
 *    no puede es pasar sin que nadie lo haya pedido.
 * 3. **Sin SMTP se dice, no se disimula.** Con el correo sin configurar el
 *    diálogo lo avisa arriba de todo y el masivo ofrece bajar los PDF en lote,
 *    que es el camino real del día de la entrega.
 * 4. **El masivo no manda a ciegas.** Primero se ve la lista completa: cuántos
 *    tienen email, cuántos no y quiénes son. Recién después se confirma.
 * 5. **El resultado se muestra entero.** Un lote con 58 enviados y 2 rebotados
 *    no es un error: se listan los dos que fallaron con su motivo y los 58 no
 *    se pierden.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Badge, Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { api } from '@/lib/api';
import type {
  EstadoEnvio,
  MailPreview,
  MassMailingPreview,
  MassMailingResult,
  MassMailingSpec,
  ModelSpec,
} from '@/lib/types';
import {
  BotonBloqueado,
  DETALLE_SUCIO,
  motivoDelServidor,
  salidaDeGuardado,
  type AccionNoDisponible,
  type EstadoDeGuardado,
} from '@/views/MotivoBloqueo';
import { Toolbar, type ToolbarItem } from '@/views/Toolbar';
import { useInvalidateModel } from '@/views/queries';

// ---------------------------------------------------------------------------
// Piezas compartidas
// ---------------------------------------------------------------------------

/**
 * Cómo se rotula un envío. `generated` es el mail que se armó pero **no salió
 * del servidor** porque no hay SMTP: se muestra distinto de `sent` a propósito,
 * porque decirle «Enviado» a algo que nadie recibió es el defecto que este
 * estado vino a cerrar.
 */
function etiquetaDeEnvio(envio: { state: EstadoEnvio; duplicated?: boolean }): string {
  if (envio.duplicated === true) return 'Ya estaba';
  if (envio.state === 'sent') return 'Enviado';
  if (envio.state === 'generated') return 'Sin salir';
  if (envio.state === 'skipped') return 'Sin email';
  return 'Error';
}

function tonoDeEnvio(envio: { state: EstadoEnvio; duplicated?: boolean }): 'success' | 'warning' | 'danger' {
  if (envio.duplicated === true) return 'success';
  if (envio.state === 'sent') return 'success';
  if (envio.state === 'generated' || envio.state === 'skipped') return 'warning';
  return 'danger';
}

const ETIQUETA = 'block text-xs font-medium uppercase tracking-wide text-fg-muted';
const CONTROL =
  'w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-fg ' +
  'placeholder:text-fg-faint focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30';

/** Cascarón del modal: fondo, foco atrapado y Escape. Lo comparten los dos. */
function Modal({
  titulo,
  subtitulo,
  onClose,
  children,
  pie,
  ancho = 'max-w-2xl',
}: {
  titulo: string;
  subtitulo?: string;
  onClose: () => void;
  children: React.ReactNode;
  pie: React.ReactNode;
  ancho?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);

  useEffect(() => {
    focoPrevio.current = document.activeElement as HTMLElement | null;
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('[data-foco-inicial]')?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-3 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onClose} aria-hidden="true" />
      {/*
        Es un `div` y no un `form`: la botonera vive DENTRO del `<form>` del
        formulario de registro, y un form anidado es HTML inválido — el submit
        del diálogo terminaría guardando el registro en vez de mandar el mail.
        Mismo criterio que `ActionDialog`.
      */}
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mail-titulo"
        className={`relative flex max-h-[92vh] w-full ${ancho} flex-col rounded-xl border border-line bg-surface shadow-lg animate-slide-up`}
      >
        <header className="border-b border-line px-5 py-4">
          <h2 id="mail-titulo" className="text-lg font-semibold">
            {titulo}
          </h2>
          {subtitulo !== undefined && subtitulo !== '' && (
            <p className="mt-0.5 truncate text-sm text-fg-muted">{subtitulo}</p>
          )}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        <footer className="flex flex-col-reverse gap-2 border-t border-line px-5 py-4 sm:flex-row sm:justify-end">
          {pie}
        </footer>
      </div>
    </div>
  );
}

/** El cartel de «no hay servidor de correo». Se dice una vez y arriba de todo. */
function AvisoSinSmtp({ texto }: { texto: string }) {
  return (
    <div className="mb-4 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-fg">
      <span className="font-semibold">Sin servidor de correo. </span>
      {texto}
    </div>
  );
}

function Cargando() {
  return <p className="py-8 text-center text-sm text-fg-muted">Preparando el mensaje…</p>;
}

function ErrorDeCarga({ error }: { error: unknown }) {
  return (
    <p className="py-8 text-center text-sm text-danger">
      {error instanceof Error ? error.message : 'No se pudo preparar el envío.'}
    </p>
  );
}

/** Chips con las variables disponibles, para que el usuario sepa qué puede escribir. */
function Variables({ variables }: { variables: { name: string; label: string; value?: string }[] }) {
  if (variables.length === 0) return null;
  return (
    <details className="mt-3 text-sm">
      <summary className="cursor-pointer text-fg-muted">Variables disponibles</summary>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {variables.map((variable) => (
          <Badge
            key={variable.name}
            tone="neutral"
            title={`${variable.label}${variable.value === undefined || variable.value === '' ? '' : `: ${variable.value}`}`}
          >
            {`{{${variable.name}}}`}
          </Badge>
        ))}
      </div>
      <p className="mt-2 text-xs text-fg-faint">
        Se escriben entre llaves dobles y el sistema las reemplaza al mandar. El texto de fábrica se
        edita en Configuración ▸ Correo ▸ Plantillas de email.
      </p>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Diálogo individual
// ---------------------------------------------------------------------------
export interface SendMailDialogProps {
  spec: ModelSpec;
  recordId: number;
  onClose: () => void;
}

export function SendMailDialog({ spec, recordId, onClose }: SendMailDialogProps) {
  const invalidar = useInvalidateModel();
  const [para, setPara] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [copiarme, setCopiarme] = useState(false);
  const [adjuntar, setAdjuntar] = useState(true);
  const [tocado, setTocado] = useState(false);

  const previa = useQuery<MailPreview>({
    queryKey: ['mail', spec.model, recordId],
    queryFn: ({ signal }) => api.mailPreview(spec.model, recordId, signal),
    // Cada apertura vuelve a pedirla: trae una `request_key` nueva, y reusar la
    // anterior haría que el segundo envío deliberado se tomara por un duplicado.
    staleTime: 0,
    gcTime: 0,
  });

  // La propuesta del servidor sólo se copia al estado UNA vez: si se recargara,
  // pisaría lo que el usuario está escribiendo.
  useEffect(() => {
    const datos = previa.data;
    if (datos === undefined || tocado) return;
    setPara(datos.to);
    setAsunto(datos.subject);
    setCuerpo(datos.body);
    setAdjuntar(datos.attach_default);
    setTocado(true);
  }, [previa.data, tocado]);

  const enviar = useMutation({
    mutationFn: () => {
      const datos = previa.data;
      if (datos === undefined) throw new Error('Todavía no se preparó el mensaje.');
      return api.mailSend(spec.model, recordId, {
        to: para,
        subject: asunto,
        body: cuerpo,
        attach: adjuntar,
        cc: copiarme && datos.cc_me !== '' ? [datos.cc_me] : [],
        request_key: datos.request_key,
      });
    },
    onSuccess: (resultado) => {
      if (resultado.duplicated) notify.warning('Ya se había enviado', resultado.message);
      else if (!resultado.sent) notify.error('No se pudo enviar', resultado.message);
      else if (!resultado.delivered) notify.warning('Se generó, pero no salió', resultado.message);
      else notify.success('Enviado', resultado.message);

      // El envío deja rastro en el chatter del registro: hay que releerlo.
      void invalidar(spec.model);
      if (resultado.sent) onClose();
    },
    onError: (error) => {
      notify.error('No se pudo enviar', error instanceof Error ? error.message : undefined);
    },
  });

  const datos = previa.data;

  /**
   * Por qué «Enviar» no se puede apretar, o `null` si se puede.
   *
   * El orden importa: el permiso es lo que no se levanta completando un campo,
   * así que va primero. Después, lo que falta cargar, **nombrado**: «no está
   * listo» no le dice a nadie qué le falta.
   */
  const motivoDeEnvio: string | null =
    datos === undefined
      ? 'Todavía se está preparando el mensaje.'
      : datos.can_send === false
        ? 'No tenés permiso para mandar este registro por email. Pedíselo a quien administra los accesos.'
        : para.trim() === '' && asunto.trim() === ''
          ? 'Falta el destinatario y el asunto.'
          : para.trim() === ''
            ? 'Falta el destinatario: completá «Para» con la dirección de correo.'
            : asunto.trim() === ''
              ? 'Falta el asunto.'
              : null;

  return (
    <Modal
      titulo={datos?.label ?? 'Enviar por email'}
      {...(datos?.record_label === undefined ? {} : { subtitulo: datos.record_label })}
      onClose={onClose}
      pie={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          {/* Tres motivos distintos y una sola consecuencia visual: el botón
              apagado. Antes el de permiso viajaba al `title` —invisible salvo
              hovereando un control muerto— y el de «falta completar» no viajaba
              a ningún lado, aunque el aviso ámbar de al lado del campo «Para»
              contara la mitad de la historia. */}
          {motivoDeEnvio === null ? (
            <Button
              variant="primary"
              loading={enviar.isPending}
              onClick={() => {
                enviar.mutate();
              }}
            >
              Enviar
            </Button>
          ) : (
            <BotonBloqueado variant="primary" label="Enviar" motivo={motivoDeEnvio} />
          )}
        </>
      }
    >
      {previa.isPending && <Cargando />}
      {previa.isError && <ErrorDeCarga error={previa.error} />}
      {datos !== undefined && (
        <>
          {!datos.smtp_configured && <AvisoSinSmtp texto={datos.smtp_warning} />}

          <div className="space-y-4">
            <div>
              <label className={ETIQUETA} htmlFor="mail-para">
                Para
              </label>
              <input
                id="mail-para"
                data-foco-inicial
                type="email"
                className={`mt-1 ${CONTROL}`}
                value={para}
                placeholder="nombre@dominio.com"
                onChange={(evento) => {
                  setPara(evento.target.value);
                }}
              />
              {datos.to === '' && datos.to_missing_reason !== '' && (
                <p className="mt-1 text-xs text-warning">{datos.to_missing_reason}</p>
              )}
            </div>

            <div>
              <label className={ETIQUETA} htmlFor="mail-asunto">
                Asunto
              </label>
              <input
                id="mail-asunto"
                type="text"
                className={`mt-1 ${CONTROL}`}
                value={asunto}
                onChange={(evento) => {
                  setAsunto(evento.target.value);
                }}
              />
            </div>

            <div>
              <label className={ETIQUETA} htmlFor="mail-cuerpo">
                Mensaje
              </label>
              <textarea
                id="mail-cuerpo"
                rows={9}
                className={`mt-1 ${CONTROL} font-normal leading-relaxed`}
                value={cuerpo}
                onChange={(evento) => {
                  setCuerpo(evento.target.value);
                }}
              />
              {datos.unknown_variables.length > 0 && (
                <p className="mt-1 text-xs text-warning">
                  La plantilla usa variables que el sistema no conoce y van a salir tal cual:{' '}
                  {datos.unknown_variables.map((nombre) => `{{${nombre}}}`).join(', ')}
                </p>
              )}
              <Variables variables={datos.variables} />
            </div>

            <div className="flex flex-col gap-2 border-t border-line pt-3 text-sm">
              {datos.can_attach && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={adjuntar}
                    onChange={(evento) => {
                      setAdjuntar(evento.target.checked);
                    }}
                  />
                  <span>
                    Adjuntar <span className="font-medium">{datos.attachment || 'el PDF'}</span>
                  </span>
                </label>
              )}
              {datos.cc_me !== '' && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={copiarme}
                    onChange={(evento) => {
                      setCopiarme(evento.target.checked);
                    }}
                  />
                  <span>Mandarme una copia a {datos.cc_me}</span>
                </label>
              )}
              {datos.from_email !== '' && (
                <p className="text-xs text-fg-faint">Sale desde {datos.from_email}</p>
              )}
            </div>

            {datos.history.length > 0 && (
              <details className="border-t border-line pt-3 text-sm">
                <summary className="cursor-pointer text-fg-muted">
                  Ya se mandó {datos.history.length === 1 ? 'una vez' : `${datos.history.length} veces`}
                </summary>
                <ul className="mt-2 space-y-1">
                  {datos.history.map((envio, indice) => (
                    <li key={`${envio.created_at}-${indice}`} className="flex flex-wrap gap-2">
                      <Badge tone={tonoDeEnvio(envio)}>{envio.state_label}</Badge>
                      <span className="text-fg-muted">{envio.to}</span>
                      <span className="text-fg-faint">
                        {new Date(envio.sent_at ?? envio.created_at).toLocaleString('es-AR')}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Diálogo del envío masivo
// ---------------------------------------------------------------------------
export interface MassMailDialogProps {
  spec: ModelSpec;
  recordId: number;
  mailing: MassMailingSpec;
  onClose: () => void;
}

/** Dispara la descarga de un blob con el nombre que trae el backend. */
function guardarArchivo(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = filename;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

export function MassMailDialog({ spec, recordId, mailing, onClose }: MassMailDialogProps) {
  const invalidar = useInvalidateModel();
  const [copiarme, setCopiarme] = useState(false);
  const [excluidos, setExcluidos] = useState<Set<string>>(new Set());
  const [reenviar, setReenviar] = useState(false);
  const [parte, setParte] = useState<MassMailingResult | null>(null);

  const previa = useQuery<MassMailingPreview>({
    queryKey: ['mailing', spec.model, recordId, mailing.name],
    queryFn: ({ signal }) => api.massMailingPreview(spec.model, recordId, mailing.name, signal),
    staleTime: 0,
    gcTime: 0,
  });

  const datos = previa.data;

  /**
   * A quiénes se les va a mandar de verdad.
   *
   * Los que ya la recibieron quedan afuera salvo que se pida el reenvío
   * explícito, y el botón dice el número real. Es lo que faltaba: antes decía
   * «Enviar a 8» las dos veces, y las dos veces mandaba ocho. La garantía sigue
   * estando en el backend —acá sólo se deja de mentir el número—, así que
   * apretar igual no duplica nada.
   */
  const seleccionados = useMemo(
    () =>
      (datos?.recipients ?? []).filter(
        (uno) => uno.sendable && !excluidos.has(uno.key) && (reenviar || !uno.already_sent),
      ),
    [datos, excluidos, reenviar],
  );

  /**
   * Por qué «Enviar a N» no se puede apretar, o `null` si se puede.
   *
   * El orden va del problema que no se levanta acá al que sí: primero el
   * permiso, después el tope del despliegue —que la lista de arriba ya
   * explica— y por último la selección vacía, que se arregla tildando una fila.
   */
  const motivoDelMasivo: string | null =
    datos === undefined
      ? 'Todavía se está preparando la lista de destinatarios.'
      : datos.can_send === false
        ? 'No tenés permiso para disparar este envío. Pedíselo a quien administra los accesos.'
        : datos.over_limit === true
          ? 'La lista supera el tope de destinatarios por envío de este sistema. El aviso de arriba dice cuál es y cómo partir la tanda.'
          : seleccionados.length === 0
            ? 'No hay ningún destinatario tildado. Los que están apagados es porque no tienen email o porque ya lo recibieron: para volver a mandárselo, marcá «reenviar».'
            : null;

  const enviar = useMutation({
    mutationFn: () => {
      if (datos === undefined) throw new Error('Todavía no se preparó el envío.');
      return api.massMailingSend(spec.model, recordId, mailing.name, {
        keys: seleccionados.map((uno) => uno.key),
        cc: copiarme && datos.cc_me !== '' ? [datos.cc_me] : [],
        request_key: datos.request_key,
        resend: reenviar,
      });
    },
    onSuccess: (resultado) => {
      setParte(resultado);
      if (resultado.failed.length > 0) notify.warning('Envío terminado con errores', resultado.message);
      else if (resultado.sent === 0) notify.warning('No salió ninguno', resultado.message);
      // Sin SMTP el lote se compuso pero no salió del servidor: el mismo criterio
      // que el envío individual («Se generó, pero no salió») y que el estado
      // «Generado, sin salir» de la bitácora. Un tilde verde y «Envío terminado»
      // sobre doce mails que nadie recibió es la pantalla diciendo que el trabajo
      // está hecho cuando falta lo único que importa: que lleguen.
      //
      // El parte del lote no trae un `delivered` propio; lo dice cada renglón con
      // su estado, que es el mismo dato del que sale la bitácora.
      else if (!resultado.results.some((uno) => uno.state === 'sent'))
        notify.warning('Se generaron, pero no salieron', resultado.message);
      else notify.success('Envío terminado', resultado.message);
      void invalidar(spec.model);
    },
    onError: (error) => {
      notify.error('No se pudo enviar', error instanceof Error ? error.message : undefined);
    },
  });

  const bajarPdfs = useMutation({
    mutationFn: () => api.downloadMassMailingPdfs(spec.model, recordId, mailing.name),
    onSuccess: ({ blob, filename }) => {
      guardarArchivo(blob, filename);
    },
    onError: (error) => {
      notify.error('No se pudieron generar los PDF', error instanceof Error ? error.message : undefined);
    },
  });

  return (
    <Modal
      ancho="max-w-3xl"
      titulo={mailing.label}
      {...(datos?.record_label === undefined ? {} : { subtitulo: datos.record_label })}
      onClose={onClose}
      pie={
        parte !== null ? (
          <Button variant="primary" onClick={onClose} data-foco-inicial>
            Cerrar
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            {datos === undefined || datos.total === 0 ? (
              <BotonBloqueado
                label="Descargar los PDF"
                motivo="No hay ningún destinatario en la lista, así que no hay PDF que generar."
              />
            ) : (
              <Button
                variant="secondary"
                loading={bajarPdfs.isPending}
                title="Genera un ZIP con un PDF por destinatario, para mandarlos a mano."
                onClick={() => {
                  bajarPdfs.mutate();
                }}
              >
                Descargar los PDF
              </Button>
            )}
            {/* Tres motivos y una sola consecuencia. El de `over_limit` ya se
                explica arriba, en la lista; los otros dos no se decían en
                ningún lado —el de permiso viajaba al `title`, que sobre un
                botón deshabilitado no existe—. */}
            {motivoDelMasivo === null ? (
              <Button
                variant="primary"
                loading={enviar.isPending}
                onClick={() => {
                  enviar.mutate();
                }}
              >
                {`Enviar a ${seleccionados.length}`}
              </Button>
            ) : (
              <BotonBloqueado
                variant="primary"
                label={`Enviar a ${seleccionados.length}`}
                motivo={motivoDelMasivo}
              />
            )}
          </>
        )
      }
    >
      {previa.isPending && <Cargando />}
      {previa.isError && <ErrorDeCarga error={previa.error} />}

      {datos !== undefined && parte === null && (
        <>
          {!datos.smtp_configured && <AvisoSinSmtp texto={datos.smtp_warning} />}

          <div className="mb-4 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md border border-line bg-surface-raised px-3 py-2">
              <p className="text-xl font-semibold">{datos.total}</p>
              <p className="text-xs uppercase tracking-wide text-fg-muted">Destinatarios</p>
            </div>
            <div className="rounded-md border border-line bg-surface-raised px-3 py-2">
              <p className="text-xl font-semibold text-success">{datos.with_email}</p>
              <p className="text-xs uppercase tracking-wide text-fg-muted">Con email</p>
            </div>
            <div className="rounded-md border border-line bg-surface-raised px-3 py-2">
              <p className={`text-xl font-semibold ${datos.without_email > 0 ? 'text-warning' : ''}`}>
                {datos.without_email}
              </p>
              <p className="text-xs uppercase tracking-wide text-fg-muted">Sin email</p>
            </div>
          </div>

          {datos.over_limit && (
            <p className="mb-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm">
              Son {datos.total} destinatarios y el tope por envío es {datos.max_recipients}. Dividilo en
              tandas o descargá los PDF.
            </p>
          )}

          {/*
            El aviso va ANTES de apretar, no después. El defecto original era
            justamente ese: los dos intentos decían «8 enviados» y la
            administradora no tenía forma de saber que los ocho propietarios
            acababan de recibir la expensa por segunda vez.
          */}
          {datos.already_sent > 0 && (
            <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
              <p>{datos.already_sent_warning}</p>
              <label className="mt-2 flex items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  checked={reenviar}
                  onChange={(evento) => {
                    setReenviar(evento.target.checked);
                  }}
                />
                <span>Reenviar a los que ya lo recibieron</span>
              </label>
            </div>
          )}

          {datos.confirm_message !== '' && (
            <p className="mb-3 text-sm text-fg-muted">{datos.confirm_message}</p>
          )}

          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[34rem] text-sm">
              <thead className="bg-surface-raised text-left text-xs uppercase tracking-wide text-fg-muted">
                <tr>
                  <th className="w-10 px-3 py-2"> </th>
                  <th className="px-3 py-2">Destinatario</th>
                  <th className="px-3 py-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {datos.recipients.map((uno) => {
                  // Ya recibió la suya y no se pidió reenvío: queda a la vista,
                  // marcado y fuera del lote. Sacarlo de la lista escondería que
                  // ya se le mandó, que es la mitad de lo que hay que decir.
                  const yaEsta = uno.already_sent && !reenviar;
                  return (
                    <tr key={uno.key} className="border-t border-line">
                      <td className="px-3 py-1.5">
                        <input
                          type="checkbox"
                          aria-label={`Incluir a ${uno.label}`}
                          disabled={!uno.sendable || yaEsta}
                          checked={uno.sendable && !yaEsta && !excluidos.has(uno.key)}
                          onChange={(evento) => {
                            setExcluidos((actual) => {
                              const copia = new Set(actual);
                              if (evento.target.checked) copia.delete(uno.key);
                              else copia.add(uno.key);
                              return copia;
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        {uno.label}
                        {/*
                          El candado es el mismo con SMTP y sin SMTP —el mail se
                          compuso, no se repite—, pero la etiqueta no puede decir
                          «Ya enviado» sobre algo que no salió del servidor: es la
                          misma distinción que la bitácora y que el aviso de
                          arriba.
                        */}
                        {uno.already_sent &&
                          (datos.smtp_configured ? (
                            <Badge tone="success" className="ml-2">
                              Ya enviado
                            </Badge>
                          ) : (
                            <Badge tone="warning" className="ml-2">
                              Ya generado
                            </Badge>
                          ))}
                      </td>
                      <td className="px-3 py-1.5 text-fg-muted">
                        {uno.sendable ? uno.to : <span className="text-warning">{uno.reason}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {datos.subject_sample !== '' && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-fg-muted">
                Vista previa del mensaje{datos.sample_of === '' ? '' : ` (${datos.sample_of})`}
              </summary>
              <p className="mt-2 font-medium">{datos.subject_sample}</p>
              <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-fg-muted">
                {datos.body_sample}
              </pre>
              <p className="mt-2 text-xs text-fg-faint">
                Cada destinatario recibe el mismo texto con SUS datos y SU PDF. Para cambiarlo:
                Configuración ▸ Correo ▸ Plantillas de email.
              </p>
            </details>
          )}

          {datos.cc_me !== '' && (
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={copiarme}
                onChange={(evento) => {
                  setCopiarme(evento.target.checked);
                }}
              />
              <span>Mandarme una copia de cada uno a {datos.cc_me}</span>
            </label>
          )}
        </>
      )}

      {parte !== null && (
        <div className="space-y-4">
          <p className="text-sm">{parte.message}</p>

          {parte.failed.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-danger">No se pudieron enviar</h3>
              <ul className="mt-1 space-y-1 text-sm">
                {parte.failed.map((fallo) => (
                  <li key={fallo.key}>
                    <span className="font-medium">{fallo.label}</span>{' '}
                    <span className="text-fg-muted">{fallo.to}</span> — {fallo.error}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {parte.skipped.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-warning">Quedaron afuera</h3>
              <ul className="mt-1 space-y-1 text-sm">
                {parte.skipped.map((omitido) => (
                  <li key={omitido.key}>
                    <span className="font-medium">{omitido.label}</span> — {omitido.reason}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {parte.pending.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold">Sin procesar (se cortó por tiempo)</h3>
              <p className="mt-1 text-sm text-fg-muted">
                {parte.pending.join(', ')}. Volvé a disparar el envío: a los que ya salieron no se les
                manda de nuevo.
              </p>
            </section>
          )}

          <details className="text-sm">
            <summary className="cursor-pointer text-fg-muted">Ver el detalle completo</summary>
            <ul className="mt-2 space-y-1">
              {parte.results.map((fila) => (
                <li key={fila.key} className="flex flex-wrap items-center gap-2">
                  <Badge tone={tonoDeEnvio(fila)}>{etiquetaDeEnvio(fila)}</Badge>
                  <span>{fila.label}</span>
                  <span className="text-fg-faint">{fila.to}</span>
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Botonera
// ---------------------------------------------------------------------------
export interface MailBarProps {
  spec: ModelSpec;
  recordId: number;
  size?: 'sm' | 'md';
  /**
   * Motivo por el que el envío está bloqueado ahora mismo (típicamente: hay
   * cambios sin guardar). El mail se arma con lo que hay en la base, no con lo
   * que se ve en pantalla: mandar un comprobante a medio editar es adjuntar
   * números que no son.
   */
  blockedReason?: string | null;
  /** El guardado de la ficha, para ofrecer la salida en el mismo clic. */
  guardado?: EstadoDeGuardado | null;
}

/**
 * Los botones de correo del formulario: «Enviar por email» y uno por cada envío
 * masivo declarado. Degrada a nada: sin `spec.mailing` no dibuja nada y ninguna
 * pantalla depende de que exista.
 *
 * Mismo criterio que `ActionBar` y `ReportBar`, que son los botones de al lado:
 * **lo que el servidor deshabilitó no se dibuja**. Un período que todavía no se
 * emitió no puede mandar las liquidaciones, y el botón vuelve solo en cuanto se
 * emite. Lo único que se muestra en gris es `blockedReason` —hay cambios sin
 * guardar—, porque eso lo levanta la persona en un clic.
 */
/**
 * Los botones de correo DESCRITOS, para que la cabecera del formulario los
 * pueda meter en la misma botonera plegable que las acciones y los reportes.
 * Ver `Toolbar` y `useActionItems`.
 */
export function useMailItems({
  spec,
  recordId,
  blockedReason = null,
  guardado = null,
}: Omit<MailBarProps, 'size'>): {
  items: ToolbarItem[];
  dialogs: ReactNode;
  ocultas: AccionNoDisponible[];
} {
  const [abierto, setAbierto] = useState<'individual' | string | null>(null);

  const mailing = spec.mailing;
  const masivos = (mailing?.mass_mailings ?? []).filter((uno) => uno.enabled !== false);
  const individual = mailing !== undefined && mailing !== null && mailing.enabled !== false;

  // Lo que el servidor apagó, con su motivo. Mismo criterio que las acciones y
  // los reportes: el botón muerto no se dibuja, la explicación sí existe.
  const ocultas: AccionNoDisponible[] = [
    ...(mailing != null && mailing.enabled === false
      ? [
          {
            clave: 'mail-individual',
            etiqueta: mailing.label,
            motivo: motivoDelServidor(mailing.disabled_reason, mailing.label),
          },
        ]
      : []),
    ...(mailing?.mass_mailings ?? [])
      .filter((uno) => uno.enabled === false)
      .map((uno) => ({
        clave: `mail-masivo-${uno.name}`,
        etiqueta: uno.label,
        motivo: motivoDelServidor(uno.disabled_reason, uno.label),
      })),
  ];

  // Mandar un mail SÍ sale del sistema: le llega a un cliente. Nunca se encadena
  // con el guardado —se guarda, el botón queda listo y lo aprieta la persona—.
  // Ver `SalidaDeBloqueo.encadena` y la regla del proyecto.
  const salidaSinEncadenar = (clave: string): ReturnType<typeof salidaDeGuardado> =>
    blockedReason === null ? null : salidaDeGuardado(guardado, { encadena: false, clave });

  const cerrar = () => {
    setAbierto(null);
  };

  const abiertoMasivo = masivos.find((uno) => uno.name === abierto);

  const items: ToolbarItem[] = [];
  if (individual && mailing != null) {
    items.push({
      key: 'mail-individual',
      label: mailing.label,
      variant: 'secondary',
      disabled: blockedReason !== null,
      title: mailing.help !== undefined && mailing.help !== '' ? mailing.help : null,
      reason: blockedReason === null ? null : `${blockedReason} ${DETALLE_SUCIO}`,
      salida: salidaSinEncadenar('mail-individual'),
      onClick: () => {
        setAbierto('individual');
      },
    });
  }
  for (const masivo of masivos) {
    items.push({
      key: `mail-masivo-${masivo.name}`,
      label: masivo.label,
      variant: 'secondary',
      disabled: blockedReason !== null,
      title: masivo.help !== undefined && masivo.help !== '' ? masivo.help : null,
      reason: blockedReason === null ? null : `${blockedReason} ${DETALLE_SUCIO}`,
      salida: salidaSinEncadenar(`mail-masivo-${masivo.name}`),
      onClick: () => {
        setAbierto(masivo.name);
      },
    });
  }

  const dialogs = (
    <>
      {abierto === 'individual' && (
        <SendMailDialog spec={spec} recordId={recordId} onClose={cerrar} />
      )}
      {abiertoMasivo !== undefined && (
        <MassMailDialog spec={spec} recordId={recordId} mailing={abiertoMasivo} onClose={cerrar} />
      )}
    </>
  );

  return { items, dialogs, ocultas };
}

/** La barra de siempre. Ver el comentario gemelo en `ActionBar`. */
export function MailBar({ spec, recordId, size = 'md', blockedReason = null }: MailBarProps) {
  const { items, dialogs } = useMailItems({ spec, recordId, blockedReason });
  return (
    <>
      <Toolbar items={items} visible={items.length} size={size} />
      {dialogs}
    </>
  );
}
